import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import SITE_CONFIG from '../config/siteConfig';
import { 
  Check, 
  Sparkles, 
  Zap, 
  Crown, 
  Gift, 
  CreditCard, 
  Shield, 
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Star,
  TrendingUp,
  Download,
  Image as ImageIcon,
  Loader
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Fetch packages from API
  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/ai/credits/packages/`);
      setPackages(response.data.results || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Map API packages to UI format
  const getIconForSlug = (slug) => {
    const iconMap = {
      'starter': Star,
      'creator': TrendingUp,
      'pro': Crown
    };
    return iconMap[slug] || Star;
  };

  const getColorForSlug = (slug) => {
    const colorMap = {
      'starter': 'blue',
      'creator': 'purple',
      'pro': 'yellow'
    };
    return colorMap[slug] || 'blue';
  };

  // Free pack (always shown first)
  const freePack = {
    id: 'free',
    name: 'Free Pack',
    subtitle: 'Try Before You Buy',
    price: '₹0',
    credits: 20,
    carousels: '2–6 carousels',
    icon: Gift,
    color: 'gray',
    popular: false,
    features: [
      'Experience the platform with real outputs',
      '20 credits included',
      'Generate 2–6 carousels',
      'High-quality Flux2-Pro images',
      'AI-generated captions included',
      'ZIP & PDF downloads',
      'No credit card required'
    ]
  };

  // Combine free pack with API packages
  const pricingPlans = [
    freePack,
    ...packages.map(pkg => ({
      id: pkg.slug,
      packageId: pkg.id,
      name: pkg.name,
      subtitle: pkg.subtitle,
      price: `₹${pkg.price}`,
      credits: pkg.credits,
      carousels: pkg.carousel_range,
      icon: getIconForSlug(pkg.slug),
      color: getColorForSlug(pkg.slug),
      popular: pkg.is_popular,
      features: pkg.features
    }))
  ];

  const howCreditsWork = [
    { icon: ImageIcon, text: '1 credit = 1 image' },
    { icon: Sparkles, text: 'Carousels require 3–10 images' },
    { icon: Check, text: 'Credits deduct only on successful generation' },
    { icon: Shield, text: 'Failed generations → credits auto-refunded' },
    { icon: Zap, text: 'Credits never expire' },
    { icon: Download, text: 'ZIP & PDF downloads included' },
    { icon: CreditCard, text: 'No monthly subscriptions required' }
  ];

  const whyChoose = [
    'No recurring billing',
    'Pay only when you generate',
    'Free Pack to try first',
    'Credits don\'t expire',
    'Designed for creators, marketers & agencies',
    'Much cheaper than hiring designers'
  ];

  const faqs = [
    {
      question: 'How many credits do I need for a carousel?',
      answer: '3–10 credits depending on number of slides.'
    },
    {
      question: 'Do credits expire?',
      answer: 'Never. Use them anytime.'
    },
    {
      question: 'Can I test before paying?',
      answer: 'Yes — Free Pack gives you 20 credits at ₹0.'
    },
    {
      question: 'Is a credit card required for free tier?',
      answer: 'No.'
    },
    {
      question: 'Will monthly subscriptions be available?',
      answer: 'Not at the moment — credit packs give more flexibility.'
    }
  ];

  const getColorClasses = (color, type = 'bg') => {
    const colors = {
      gray: {
        bg: 'bg-gray-600',
        text: 'text-gray-600',
        border: 'border-gray-600',
        bgLight: 'bg-gray-50',
        hover: 'hover:bg-gray-700'
      },
      blue: {
        bg: 'bg-blue-600',
        text: 'text-blue-600',
        border: 'border-blue-600',
        bgLight: 'bg-blue-50',
        hover: 'hover:bg-blue-700'
      },
      purple: {
        bg: 'bg-purple-600',
        text: 'text-purple-600',
        border: 'border-purple-600',
        bgLight: 'bg-purple-50',
        hover: 'hover:bg-purple-700'
      },
      yellow: {
        bg: 'bg-yellow-500',
        text: 'text-yellow-600',
        border: 'border-yellow-500',
        bgLight: 'bg-yellow-50',
        hover: 'hover:bg-yellow-600'
      }
    };
    return colors[color][type];
  };

  const handleSelectPlan = async (plan) => {
    // Free plan - just signup
    if (plan.id === 'free') {
      if (!isAuthenticated) {
        navigate('/register');
      } else {
        alert('You already have free credits! Check your dashboard.');
        navigate('/dashboard');
      }
      return;
    }

    // Paid plans - require authentication
    if (!isAuthenticated) {
      alert('Please login to purchase credits');
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }

    // Initiate purchase
    await handlePurchase(plan);
  };

  const handlePurchase = async (plan) => {
    setPurchasing(true);

    try {
      const token = localStorage.getItem('access_token');

      // Step 1: Initiate purchase
      const initiateResponse = await axios.post(
        `${API_BASE_URL}/ai/credits/purchase/initiate/`,
        { package_id: plan.packageId },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!initiateResponse.data.success) {
        throw new Error('Failed to initiate purchase');
      }

      const {
        purchase_id,
        razorpay_order_id,
        amount,
        currency,
        razorpay_key_id,
        package: packageInfo
      } = initiateResponse.data;

      // Step 2: Open Razorpay checkout
      const options = {
        key: razorpay_key_id,
        amount: amount * 100, // Convert to paise
        currency: currency,
        name: 'AI Carousel Maker',
        description: `${packageInfo.name} - ${packageInfo.credits} credits`,
        order_id: razorpay_order_id,
        handler: async (razorpayResponse) => {
          // Step 3: Confirm purchase
          try {
            const confirmResponse = await axios.post(
              `${API_BASE_URL}/ai/credits/purchase/confirm/`,
              {
                purchase_id: purchase_id,
                razorpay_order_id: razorpay_order_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_signature: razorpayResponse.razorpay_signature,
                forfeit_confirmed: true
              },
              {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              }
            );

            if (confirmResponse.data.success) {
              await refreshUser(); // re-fetch profile so isPaidUser updates immediately
              alert(`🎉 Payment successful! ${confirmResponse.data.credits_added} credits added to your account.`);
              navigate('/dashboard');
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (error) {
            console.error('Error confirming payment:', error);
            alert('❌ Payment verification failed. Please contact support with your payment ID: ' + razorpayResponse.razorpay_payment_id);
          } finally {
            setPurchasing(false);
          }
        },
        prefill: {
          email: user?.email || '',
          contact: user?.phone || ''
        },
        theme: {
          color: '#8B5CF6'
        },
        modal: {
          ondismiss: () => {
            setPurchasing(false);
          }
        }
      };

      // Check if Razorpay is loaded
      if (!window.Razorpay) {
        alert('❌ Payment gateway not loaded. Please refresh and try again.');
        setPurchasing(false);
        return;
      }

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        alert('❌ Payment failed: ' + response.error.description);
        setPurchasing(false);
      });
      rzp.open();

    } catch (error) {
      console.error('Error initiating purchase:', error);
      alert('❌ Failed to initiate purchase. Please try again.');
      setPurchasing(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Pricing Plans - AI Carousel Generator</title>
        <link rel="canonical" href={`${SITE_CONFIG.siteUrl}/pricing`} />
        <meta 
          name="description" 
          content="Choose the perfect plan for your content creation needs. Flexible pricing with credits-based system. Pay only for what you generate." 
        />
        <meta 
          name="keywords" 
          content="pricing, plans, credits, subscription, AI carousel pricing, pay as you go, content creation pricing" 
        />
        <meta property="og:title" content="Pricing Plans - AI Carousel Generator" />
        <meta property="og:description" content="Flexible pricing for all your content needs. Pay only for what you generate." />
        <meta property="og:url" content={`${SITE_CONFIG.siteUrl}/pricing`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pricing Plans - AI Carousel" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center mb-4">
            <Sparkles className="h-12 w-12" />
          </div>
          <h1 className="text-5xl font-bold mb-4">
            Pricing That Fits Every Creator
          </h1>
          <p className="text-xl text-purple-100 mb-2">
            Pay only for what you generate.
          </p>
          <p className="text-2xl font-semibold">
            1 image = 1 credit.
          </p>
          <p className="text-lg text-purple-100 mt-2">
            No subscriptions. No hidden fees.
          </p>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-6 -mt-10">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader className="h-12 w-12 text-purple-600 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan) => {
            const Icon = plan.icon;
            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 flex flex-col ${
                  plan.popular ? 'ring-4 ring-purple-500' : ''
                }`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                    MOST POPULAR
                  </div>
                )}

                {/* Card Header */}
                <div className={`${getColorClasses(plan.color, 'bgLight')} p-6 text-center`}>
                  <div className={`inline-flex items-center justify-center w-16 h-16 ${getColorClasses(plan.color, 'bg')} rounded-full mb-4`}>
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">
                    {plan.name}
                  </h3>
                  <p className={`text-sm font-medium ${getColorClasses(plan.color, 'text')}`}>
                    {plan.subtitle}
                  </p>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gray-900">
                      {plan.price}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {plan.credits} credits
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {plan.carousels}
                  </p>
                </div>

                {/* Features */}
                <div className="p-6 flex-grow flex flex-col">
                  <ul className="space-y-3 flex-grow">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className={`h-5 w-5 ${getColorClasses(plan.color, 'text')} mr-2 flex-shrink-0 mt-0.5`} />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={purchasing}
                    className={`w-full mt-6 ${getColorClasses(plan.color, 'bg')} ${getColorClasses(plan.color, 'hover')} text-white py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {purchasing ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <span>{plan.id === 'free' ? 'Start Free' : 'Buy Now'}</span>
                        <Zap className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* How Credits Work */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <CreditCard className="h-10 w-10 text-purple-600" />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            How Credits Work
          </h2>
          <p className="text-xl text-gray-600">
            A simple system designed for flexibility.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {howCreditsWork.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div
                key={idx}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mb-4">
                  <Icon className="h-6 w-6 text-purple-600" />
                </div>
                <p className="text-gray-700 font-medium">{item.text}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Why Choose Credit-Based Pricing */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <Gift className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">
              Why Choose Credit-Based Pricing?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {whyChoose.map((reason, idx) => (
              <div
                key={idx}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-center space-x-3"
              >
                <Check className="h-6 w-6 text-green-300 flex-shrink-0" />
                <span className="text-white font-medium">{reason}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Refund Policy */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center mb-6">
            <Shield className="h-8 w-8 text-green-600 mr-3" />
            <h2 className="text-3xl font-bold text-gray-900">
              Refund Policy
            </h2>
          </div>
          <p className="text-lg text-gray-600 mb-6">
            Simple and transparent:
          </p>
          <ul className="space-y-4">
            <li className="flex items-start">
              <Check className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                Refunds allowed for unused credit packs within 7 days
              </span>
            </li>
            <li className="flex items-start">
              <Check className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                If any credit from the pack is used → pack is non-refundable
              </span>
            </li>
            <li className="flex items-start">
              <Check className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                Failed generations → credits are returned automatically
              </span>
            </li>
            <li className="flex items-start">
              <Check className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                Free credits are non-refundable
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-6 pb-20">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <HelpCircle className="h-10 w-10 text-purple-600" />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className="bg-white rounded-xl shadow-lg overflow-hidden"
            >
              <button
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-200"
              >
                <span className="font-semibold text-gray-900 pr-4">
                  {faq.question}
                </span>
                {openFaq === idx ? (
                  <ChevronUp className="h-5 w-5 text-purple-600 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                )}
              </button>
              {openFaq === idx && (
                <div className="px-6 pb-4">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Create Amazing Carousels?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Start with 20 free credits. No credit card required.
          </p>
          <Link
            to="/login"
            className="bg-white text-purple-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors duration-200 inline-flex items-center space-x-2"
          >
            <span>Start Free Now</span>
            <Sparkles className="h-5 w-5" />
          </Link>
        </div>
      </div>
      </div>
    </>
  );
};

export default PricingPage;
